/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-wrapper-object-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { LogLevel } from "../logLevel";
import { CLASS_METADATA_KEY, ClassMetadata } from "./classMetadata";
import { getLogger, logAtLevel, stringify } from "./logInvocation";
import { LoggerParamMetadata, PROPERTY_KEY_LOG_VALUES } from "./paramMetadata";

interface Options
{
    readonly level: LogLevel;
    readonly logGet?: boolean;
    readonly logSet?: boolean;
    // readonly summarizeParamsForResult?: boolean;
    // readonly multiline?: boolean;
}

export function LogAccess(level: LogLevel): PropertyDecorator;
export function LogAccess(levelOrOptions: Options): PropertyDecorator;
export function LogAccess(levelOrOptions: Options | LogLevel): PropertyDecorator
{
    let level: LogLevel;
    // let multiline: boolean = false;
    let logGet: boolean = false;
    let logSet: boolean = false;
    if (typeof levelOrOptions === "object")
    {
        level = levelOrOptions.level;
        // multiline = levelOrOptions.multiline;
        logGet = levelOrOptions.logGet !== false;
        logSet = levelOrOptions.logSet !== false;
    }
    else
    {
        level = levelOrOptions;
        logGet = true;
        logSet = true;
    }

    return logAccess as PropertyDecorator;

    function logAccess(target: unknown, key: string | symbol, descriptor: TypedPropertyDescriptor<unknown>): TypedPropertyDescriptor<unknown> | void
    {
        if (descriptor === undefined)
        {
            throw new Error(`Method Decorator cannot be created with out descriptor!`);
        }

        const targetName = getTargetName(target);
        const classMetadata: ClassMetadata = Reflect.getOwnMetadata(CLASS_METADATA_KEY, target) || {};
        const propertyName = key.toString();

        if (logGet)
        {
            descriptor.get = logGetAccess(descriptor.get, targetName, propertyName, classMetadata);
        }

        if (logSet)
        {
            descriptor.set = logSetAccess(descriptor.set, target, targetName, propertyName, classMetadata);
        }

        // return descriptor;
    }

    function logGetAccess<TReturn>(getter: () => TReturn, targetName: string, propertyName: string, classMetadata: ClassMetadata): () => TReturn
    {
        if (!getter)
        {
            return undefined;
        }

        return _logGetAccess;

        function _logGetAccess(): TReturn
        {
            const log = getLogger(this, targetName, classMetadata);
            if (!log.enabled || level > log.getLogLevel())
            {
                return getter.apply(this);
            }

            try
            {
                const result = getter.apply(this);
                const message = `get_${propertyName}()`;
                logAtLevel(log, level, message, { value: result });

                return result;
            }
            catch (error)
            {
                const message = `<--- get_${propertyName}() => ${stringify(error)}`;
                logAtLevel(log, LogLevel.ERROR, message, error);
                throw error;
            }

        }
    }

    function logSetAccess<T>(setter: (value: T) => void, target: Object, targetName: string, propertyName: string, classMetadata: ClassMetadata): (value: T) => void
    {
        if (!setter)
        {
            return undefined;
        }

        return _logSetAccess;

        function _logSetAccess(value: T): void
        {
            const log = getLogger(this, targetName, classMetadata);
            if (!log.enabled || level > log.getLogLevel())
            {
                return setter.apply(this, [value]);
            }

            const logParamSettings: Array<LoggerParamMetadata> = Reflect.getOwnMetadata(PROPERTY_KEY_LOG_VALUES, target, propertyName) || [];
            const paramStr = logParamSettings.length > 0
                ? logParamSettings[0].name.toString()
                : stringify(value);

            const message = `set_${propertyName}(${paramStr})`;
            logAtLevel(log, level, message, { value });

            try
            {
                setter.apply(this, [value]);
            }
            catch (error)
            {
                const message = `<--- set_${propertyName}(${value}) => ${stringify(error)}`;
                logAtLevel(log, LogLevel.ERROR, message, error);
                throw error;
            }

        }
    }

    function getTargetName(target: any): string
    {
        if (!target)
        {
            return "UNDEFINED";
        }

        if (target.constructor?.name)
        {
            return target.constructor.name;
        }

        const prototype = Object.getPrototypeOf(target);
        if (prototype.constructor?.name)
        {
            return prototype.constructor.name;
        }

        if (target.name)
        {
            return target.name;
        }
        return "UNKNOWN";
    }
}
